import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { payloadClient } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { LoadingSpinner } from '@/shared/components/ui';

/**
 * Type for Microsoft error i18n keys.
 */
type MicrosoftErrorKey =
  | 'auth.microsoftErrors.tenantMismatch'
  | 'auth.microsoftErrors.msaBlocked'
  | 'auth.microsoftErrors.userNotFound'
  | 'auth.microsoftErrors.userNotAssigned'
  | 'auth.microsoftErrors.inviteInvalid'
  | 'auth.microsoftErrors.inviteNotFound'
  | 'auth.microsoftErrors.inviteInvalidRole'
  | 'auth.microsoftErrors.inviteInvalidGroup'
  | 'auth.microsoftErrors.invalidState'
  | 'auth.microsoftErrors.missingParams'
  | 'auth.microsoftErrors.missingToken'
  | 'auth.microsoftErrors.tokenError'
  | 'auth.microsoftErrors.accessDenied'
  | 'auth.microsoftErrors.consentRequired'
  | 'auth.microsoftErrors.authError'
  | 'auth.microsoftErrors.userError'
  | 'auth.microsoftErrors.loginFailed'
  | 'auth.microsoftErrors.unknown';

/**
 * Mapping from backend error codes to i18n keys.
 */
const ERROR_CODE_MAP: Record<string, MicrosoftErrorKey> = {
  // Tenant errors
  tenant_mismatch: 'auth.microsoftErrors.tenantMismatch',
  msa_blocked: 'auth.microsoftErrors.msaBlocked',

  // User errors
  user_not_found: 'auth.microsoftErrors.userNotFound',
  user_not_assigned: 'auth.microsoftErrors.userNotAssigned',

  // Invite errors
  invite_invalid: 'auth.microsoftErrors.inviteInvalid',
  invite_not_found: 'auth.microsoftErrors.inviteNotFound',
  invite_invalid_role: 'auth.microsoftErrors.inviteInvalidRole',
  invite_invalid_group: 'auth.microsoftErrors.inviteInvalidGroup',

  // General errors
  invalid_state: 'auth.microsoftErrors.invalidState',
  missing_params: 'auth.microsoftErrors.missingParams',
  token_error: 'auth.microsoftErrors.tokenError',
  access_denied: 'auth.microsoftErrors.accessDenied',
  consent_required: 'auth.microsoftErrors.consentRequired',
  auth_error: 'auth.microsoftErrors.authError',
  user_error: 'auth.microsoftErrors.userError',
};

function getErrorKey(errorCode: string): MicrosoftErrorKey {
  return ERROR_CODE_MAP[errorCode] || 'auth.microsoftErrors.unknown';
}

/**
 * Handles the Microsoft OAuth callback.
 *
 * This component processes the callback from Microsoft OAuth:
 * - On success: stores the JWT token and redirects to the target page
 * - On error: displays a localized error message with a link back to login
 */
export function MicrosoftCallback() {
  const { t } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { updateUser } = useAuth();

  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  // Use ref to track if callback has been processed to avoid double execution
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      const queryParams = new URLSearchParams(location.search);
      const token = queryParams.get('token');
      const errorCode = queryParams.get('error');
      const redirectPath = queryParams.get('redirectPath') || '/';

      // Error from backend?
      if (errorCode) {
        setError(t(getErrorKey(errorCode)));
        setIsProcessing(false);
        return;
      }

      // Token present?
      if (!token) {
        setError(t('auth.microsoftErrors.missingToken'));
        setIsProcessing(false);
        return;
      }

      try {
        // Store token in localStorage
        localStorage.setItem('jwtToken', token);

        // Update PayloadClient token
        payloadClient.token = token;

        // Fetch user data
        const meData = await payloadClient.me();

        if (meData?.user) {
          // Update auth context and navigate immediately
          // React's state update batching ensures proper order
          updateUser(meData.user);
          navigate(redirectPath, { replace: true });
        } else {
          throw new Error('No user data received');
        }
      } catch (_err) {
        // Remove token on error
        localStorage.removeItem('jwtToken');
        payloadClient.token = null;

        setError(t('auth.microsoftErrors.loginFailed'));
        setIsProcessing(false);
      }
    };

    processCallback();
  }, [location.search, navigate, updateUser, t]);

  if (isProcessing) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4">
        <LoadingSpinner />
        <p className="text-muted-foreground">{t('auth.processingMicrosoftLogin')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 text-center">
        <h1 className="text-2xl font-bold text-destructive">{t('auth.microsoftLoginFailed')}</h1>
        <p className="text-muted-foreground">{error}</p>
        <div className="space-y-2">
          <Link to="/auth/login" className="block text-sm underline">
            {t('auth.backToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  // Should not be reached since we either redirect or show error
  return null;
}
